import { useEffect } from 'react';

import { pauseCallRecording, resumeCallRecording } from 'common/services/dialpad-recording';

/**
 * Pauses Dialpad call recording while a payment-entry form (full CC or ACH number) is on screen, and
 * resumes it when the form unmounts. See https://github.com/gobranch/branch/issues/15438.
 *
 * Fires for every staff user — the backend resolves the caller's Dialpad user by their Cognito email
 * and no-ops if they aren't a Branch Dialpad user or aren't on a call, so the "who is on Branch's
 * Dialpad" filter lives server-side. This covers agency / mutual (Forreston, CFM, Ember) staff who
 * operate on Branch's Dialpad, not just internal agents.
 *
 * Ref-counted at module scope so overlapping payment fields (e.g. checkout showing both a card and
 * an ACH form) only pause recording once and resume only when the last one closes.
 *
 * @param {boolean} isActive whether the consuming payment field is currently shown (default true).
 *   Pass a condition (e.g. only when the unmasked input is rendered) to avoid pausing for masked
 *   last-4 displays.
 */

let activeCount = 0;

// Exported for test isolation only — resets the module-level ref count between tests.
export function _resetActiveCount() {
  activeCount = 0;
}

export function useCallRecordingPause(isActive = true) {
  useEffect(() => {
    if (!isActive) {
      return undefined;
    }

    // Clamp to guard against a stuck negative/positive count if a prior cleanup was skipped.
    activeCount = Math.max(0, activeCount) + 1;
    if (activeCount === 1) {
      pauseCallRecording();
    }

    return () => {
      activeCount = Math.max(0, activeCount - 1);
      if (activeCount === 0) {
        resumeCallRecording();
      }
    };
  }, [isActive]);
}
